import clsx from "clsx";
import { format } from "date-fns";
import React, {
  FormEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import i18n from "../../i18n/config";
import {
  LocalizedGroupName,
  lookupDateFNSLocale,
  lookupGroupName,
  lookupListingStatus,
  lookupTaskPriority,
  lookupTaskStatus,
  lookupTaskSubType,
  lookupTaskType,
  supportedLocaleCode,
} from "../../i18n/locUtils";
import { useCache } from "../cache-provider";
import useMakeSFRequest from "../request/salesforce/make-sf-call";
import { translateText, TranslationResponse } from "../service/cloud-functions";
import { useFileOperations } from "../utils/file-operations";
import { createDateLabelForTask } from "../utils/create-date-label-for-task";
import { createListingDescription } from "../utils/create-listing-description";
import { entryParkingListingFromTask } from "../utils/entry-parking-from-task-related";
import {
  checkTaskLocalized,
  dynamicLocManager,
  DynamicTranslator,
  feedLocCache,
  resetToDefaultText,
  setTaskLocPreference,
  updateLocalizedTaskModel,
} from "../utils/dynamic-string-table";
import { listingStatusColor } from "../utils/listing-status-color";
import {
  convertBillable,
  convertCurrency,
} from "../utils/maintenance-task-utils";
import { taskStatusColor } from "../utils/task-status-color";
import { Attachment } from "./attachment";
import { useAuth } from "./auth/firebase-context";
import {
  ListingEntryParkingContent,
  listingHasEntryParkingInfo,
} from "./listing-entry-parking-content";
import { LoadingAttachments } from "./loading-attachments";
import Modal from "./modal";
import { createTaskBedConfig } from "./number-of-beds";
import { pluralize } from "../utils/pluralize";
import { PostTask } from "./post-task";
import {
  ConfirmActionDialog,
  PrimaryActions,
  SecondaryActions,
  TimedActions,
} from "./task-actions";
import { TaskFormType } from "./task-form";
import Tooltip from "./tooltip";
import Badge from "./badge";

function TaskEntryParkingLink({
  listing,
  modalOpen,
  setModalOpen,
  className = "col-span-2",
  entryInstructionsLabel,
}: {
  listing: ApiListing;
  modalOpen: boolean;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  className?: string;
  entryInstructionsLabel?: string;
}) {
  const { t } = useTranslation();
  if (!listingHasEntryParkingInfo(listing)) {
    return null;
  }
  return (
    <div className={clsx("flex flex-col", className)}>
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        className="inline-flex items-center gap-1 self-start underline text-lg leading-6 text-primary-main hover:text-secondary-main cursor-pointer text-left"
      >
        <span className="icon-[fa6-solid--map-location-dot] shrink-0" />
        {t("task_detail_label_parking_and_entry")}
      </button>
      <Modal
        open={modalOpen}
        setOpen={setModalOpen}
        title={
          <span className="inline-flex items-center justify-center gap-1.5 text-lg">
            <span className="icon-[fa6-solid--map-location-dot] shrink-0" />
            {t("task_detail_label_location")}
          </span>
        }
        hideButtons
      >
        <ListingEntryParkingContent
          listing={listing}
          entryInstructionsLabel={entryInstructionsLabel}
        />
      </Modal>
    </div>
  );
}

type TaskDetailsProps = {
  id: string;
  task: ApiTask;
  taskPermission: {
    is_my_task: boolean;
    is_manageable_by_user: boolean;
    is_claimable_by_user: boolean;
    can_reassign_tasks: boolean;
  };
  refreshTask(taskData: any): Promise<void>;
  drawerProps?: { onClose(): void };
  pageProps?: { isPage: true };
};

export const TaskDetails: React.FC<TaskDetailsProps> = ({
  id,
  task,
  taskPermission,
  drawerProps,
  pageProps,
  refreshTask,
}) => {
  const { t } = useTranslation();
  const [showTranslateLink, setShowTranslateLink] = useState(
    i18n.resolvedLanguage !== supportedLocaleCode.en,
  );
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [totalTranslations, setTotalTranslations] = useState<number>(0); // render stepper
  const { user: curUser } = useAuth();
  const { allQueues } = useCache();

  // attempt to re-apply saved localization on load
  useEffect(() => {
    updateLocalizedTaskModel(task);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClose = () => {
    setAttachments([]);
    if (drawerProps) {
      drawerProps.onClose();
    }
  };
  const { additional_housekeepers, related } = task;
  const bedConfig = useMemo(
    () =>
      createTaskBedConfig(
        task.id,
        related?.king_bed_count || 0,
        related?.queen_bed_count || 0,
        related?.full_bed_count || 0,
        related?.single_bed_count || 0,
        // eslint-disable-next-line react-hooks/exhaustive-deps
      ),
    [task],
  );
  const beddingLabel = useMemo(
    () =>
      bedConfig
        .filter(({ count }) => count > 0)
        .map((config) => `${config.count}${config.icon}`)
        .join(" | "),
    [bedConfig],
  );
  const [action, setAction] = useState<ActionT>(null);
  const [followUpTask, setFollowUpTask] = useState(false);
  const [openEditTask, setOpenEditTask] = useState(false);
  const [parkingAccessModalOpen, setParkingAccessModalOpen] = useState(false);
  const [selectedSecondaryAction, setSelectedSecondaryAction] = useState({
    title: t("task_command_defer"),
    action: "deferTask",
    description: t("task_command_defer_explain"),
  });
  const request = useMakeSFRequest();
  const {
    downloadFile,
    createFileWrapper: createFileWrapperFromBlob,
    listFiles,
    unlinkFile,
    uploadFile,
  } = useFileOperations();

  // Helper function to convert File to our wrapper format
  const createFileWrapper = (file: File) => ({
    name: file.name,
    type: file.type,
    lastModified: file.lastModified,
    size: file.size,
    stream: file.stream.bind(file),
    arrayBuffer: file.arrayBuffer.bind(file),
    slice: file.slice.bind(file),
    text: file.text.bind(file),
    _originalFile: file,
    id: undefined,
    version: undefined,
    isLatest: undefined,
  });

  // messages
  const [feedMessages, setFeedMessages] = useState<FeedItemT[]>([]);
  const [postMessageLoading, setPostMessageLoading] = useState<boolean>(false);
  const [postMessageText, setPostMessageText] = useState<string>("");
  const [attachments, setAttachments] = useState<
    {
      name: string;
      type: string;
      lastModified: number;
      size: number;
      stream: () => ReadableStream;
      arrayBuffer: () => Promise<ArrayBuffer>;
      slice: (start?: number, end?: number, contentType?: string) => Blob;
      text: () => Promise<string>;
      _originalFile: File;
      id?: string;
      version?: number;
      isLatest?: boolean;
    }[]
  >([]);
  const [numberAttachments, setNumberAttachments] = useState(0);
  const [attachmentsLoading, setAttachmentsLoading] = useState(true);
  const [attachmentsMetaLoading, setAttachmentsMetaLoading] = useState(true);
  const [getMessagesLoading, setGetMessagesLoading] = useState<boolean>(false);
  const [getMessagesError, setGetMessagesError] = useState<string | null>(null);
  const [postMessageError, setPostMessageError] = useState<string | null>(null);
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null);
  const [locLoading, setLocLoading] = useState<boolean>(false);
  // const isNextReservationOwner = related !== undefined && related.next_check_in_type === 'Owner';
  const hasAdditionalHousekeepers =
    additional_housekeepers && additional_housekeepers.length > 0;
  const hasHouseman = task.related?.hasOwnProperty("houseman") || false; // we always show houseman on task drawer
  const hasInspector =
    task.sub_type === "Glass & Lanai"
      ? task.related?.hasOwnProperty("inspector")
      : false;
  const hasEngineer =
    task.sub_type === "Departure Clean"
      ? task.related?.hasOwnProperty("engineer")
      : false;
  const isCase = task.related?.hasOwnProperty("case_number") || false;
  const hasPreviousHousekeeper =
    task.type === "Inspection" &&
    task.sub_type === "Final" &&
    task.related?.previous_housekeeper;
  const showBedsInfo =
    task.related && (task.type === "Clean" || task.type === "Drop");
  const showMergeBeds =
    task?.sub_type &&
    ["Departure Clean", "Drop & Strip"].includes(task.sub_type) &&
    task.related.split_beds &&
    !task.related?.next_split_beds;
  const showSplitBeds =
    task?.sub_type &&
    ["Departure Clean", "Drop & Strip"].includes(task.sub_type) &&
    !!task.related?.next_split_beds;

  const entryParkingListing = useMemo(
    () => entryParkingListingFromTask(task),
    [task],
  );

  const getAttachmentsRequest = async () => {
    if (!task) {
      return;
    }
    setNumberAttachments(0);
    setAttachmentsError(null);
    setAttachmentsMetaLoading(true);

    // Use the centralized file operations utility
    const listResult = await listFiles(task.id);

    if (!listResult.success) {
      setAttachmentsError(t("feed_error_fetch_attachments_for_task"));
      setAttachmentsMetaLoading(false);
      return;
    }

    const files = listResult.data || [];
    setNumberAttachments(files.length);
    setAttachmentsMetaLoading(false);
    setAttachmentsLoading(true);

    const attachments = await Promise.all(
      files.map(async (file: any) => {
        const downloadResult = await downloadFile(
          file.content_document_id,
          file.content_type,
        );

        if (!downloadResult.success) {
          setAttachmentsLoading(false);
          return null;
        }

        const response = downloadResult.data as Response;
        const blob = await response.blob();

        // Use shared utility to create file wrapper with proper MIME type
        const fileWrapper = await createFileWrapperFromBlob(blob, file);

        return fileWrapper;
      }),
    );

    setAttachments(attachments.filter(Boolean)); // Remove null entries
    setAttachmentsLoading(false);
  };
  const getMessagesRequest = async () => {
    if (!task) {
      return;
    }
    setGetMessagesLoading(true);
    setGetMessagesError(null);

    const translated = checkTaskLocalized(task);
    const response = await request({
      endpointPath: `/api/feed/${task.id}`,
      method: "GET",
    });
    if (response.error) {
      setGetMessagesError(t("task_error_get_feed_messages"));
      setGetMessagesLoading(false);
      return;
    }

    // always update the base, English feed items in the cache
    const items = (response as any).feed_items;
    if (Array.isArray(items) && items.length > 0) {
      items.forEach((item: any, i: number) => {
        feedLocCache[supportedLocaleCode.en].set(item.id, item.body);
        if (translated) {
          //TODO: ! this needs to expire, because the cache is inherently stale
          const translatedBody = feedLocCache[
            i18n.resolvedLanguage as supportedLocaleCode
          ].get(item.id);
          if (translatedBody) {
            item.body = translatedBody;
          }
          //else fail silently; the state will be handled by useeffect
        }
      });
    }

    setFeedMessages((response as any).feed_items);
  };
  const postMessage = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!task || !postMessageText) {
      return;
    }
    setPostMessageError(null);
    setPostMessageLoading(true);
    try {
      const response = await request({
        endpointPath: `/api/feed/${task.id}`,
        method: "POST",
        body: JSON.stringify({
          body: postMessageText,
          is_rich_text: false,
          type: "TextPost",
          parent_id: task.id,
        }),
      });
      if (response.error) {
        setPostMessageError(t("task_error_post_feed_message"));
        setPostMessageLoading(false);
        return;
      }

      setFeedMessages((prev) => [
        ...prev,
        {
          ...(response as any).feed_item,
          owner_id: curUser?.sf_user_id,
          owner_name: curUser?.name,
          modstamp: new Date().toJSON(),
        },
      ]);
    } catch (e) {
      setPostMessageError(t("task_error_post_feed_message"));
      setPostMessageLoading(false);
      return;
    }
    setPostMessageText("");
    setPostMessageLoading(false);
  };
  useEffect(() => {
    Promise.all([getMessagesRequest(), getAttachmentsRequest()]);
  }, [task]);
  const onDeleteAttachment = useCallback(
    async (fileName: string, attachment_id?: string) => {
      if (attachment_id) {
        // Use the centralized file operations utility for smart deletion
        await unlinkFile(attachment_id, task.id);
      }
      setAttachments((prev) => {
        return prev.filter((file) => {
          return file.name !== fileName;
        });
      });
    },
    [task.id, unlinkFile],
  );
  //NOTE: We can't add attachments to a task that's assigned to a group due to conflicting 500 issues.
  //      Attachment owner must match task owner, but attachment owner cannot be a Group.
  const queueIds =
    allQueues.success && allQueues.data
      ? allQueues.data.map((queue: any) => queue.id)
      : [];
  const uploadAllowed = !queueIds.includes(task.assignee_id);
  const deleteAllowed =
    curUser &&
    [task.assignee_id, task.reporter_id].includes(curUser.sf_user_id);
  const shouldLookAtNextCheckInType =
    task.sub_type === "Departure Clean" ||
    (task.sub_type === "Drop & Strip" &&
      task.related &&
      task.related.sub_type !== "Tidy");
  const guestTypeLabel = task.related
    ? shouldLookAtNextCheckInType
      ? task.related.next_check_in_type
      : task.related.guest_type
    : "n/a";

  const handleTranslateClick = async (e: SyntheticEvent) => {
    e.preventDefault(); // do not navigate (we only use a link for cosmetics)

    const translated = checkTaskLocalized(task);
    if (translated) {
      // we are restoring the original, untranslated strings
      const errorMsg = resetToDefaultText(task, feedMessages);
      if (errorMsg) {
        setTranslationError(t("task_translate_error_bad_state"));
        return;
      }
      setTotalTranslations(totalTranslations + 1); // used because we don't have a 'loading' bool for this instant reset
      setTaskLocPreference(task, supportedLocaleCode.en);
      return;
    }
    if (!i18n.resolvedLanguage) {
      setTranslationError(t("task_translate_error_not_ready"));
      return;
    }
    setLocLoading(true);
    setTranslationError(null);

    //TODO: ! SHOW A LITTLE SPINNER

    // gather the task's string fields for dynamic translation
    const translator = new DynamicTranslator();
    translator.AddTask(task);
    if (translator.hasTextToTranslate) {
      // lazy-create the string table for this task
      if (!dynamicLocManager.task[task.id]) {
        dynamicLocManager.task[task.id] = {} as any;
        dynamicLocManager.task[task.id][supportedLocaleCode.en] =
          translator.GetEnglishMap();
      }
      // send these strings to the cloud translation service
      const translateResponse = await translateText(
        translator.GetEnglishValues(),
        i18n.language,
      );
      if (translateResponse.success) {
        const valuesTranslated = (translateResponse as TranslationResponse)
          .translation;
        if (
          Array.isArray(valuesTranslated) &&
          valuesTranslated.length === translator.CountStrings()
        ) {
          setTaskLocPreference(
            task,
            i18n.resolvedLanguage as supportedLocaleCode,
          );
          const localizedMap = translator.LocalizeTask(
            valuesTranslated,
            task,
            i18n.resolvedLanguage as supportedLocaleCode,
          );
          dynamicLocManager.task[task.id][
            i18n.resolvedLanguage as supportedLocaleCode
          ] = localizedMap;

          // translate any feed items
          const feedTextById = new Map<string, string>();
          feedMessages.forEach((item: any) => {
            feedTextById.set(item.id, item.body);
          });
          const outgoing = Array.from(feedTextById.values());
          if (outgoing.length > 0) {
            const translateResponse = await translateText(
              outgoing,
              i18n.language,
            );
            if (translateResponse.success) {
              const valuesTranslated = (
                translateResponse as TranslationResponse
              ).translation;
              if (
                Array.isArray(valuesTranslated) &&
                valuesTranslated.length === outgoing.length
              ) {
                feedMessages.forEach((item: any, i: number) => {
                  item.body = valuesTranslated[i];
                  feedLocCache[
                    i18n.resolvedLanguage as supportedLocaleCode
                  ].set(item.id, valuesTranslated[i]);
                });
              } else {
                setTranslationError(t("task_error_localize_feed_failed"));
              }
            } else {
              setTranslationError(t("task_error_localize_feed_invalid"));
            }
          }
        } else {
          setTranslationError(t("task_translate_error_invalid"));
        }
      } else {
        setTranslationError(t("task_translate_error_failed"));
      }
    }
    setLocLoading(false);
  };

  return (
    <>
      <div className="h-full w-dvw sm:w-full overflow-y-auto bg-white dark:!bg-gray-900 p-4 pt-6 animate-fade-in">
        <br className="sm:hidden" />
        <div id="task-title-status-section" className="flex flex-col">
          <div className="flex flex-row items-start">
            <h2
              className={clsx(
                "grow flex text-base font-semibold text-primary-main dark:text-secondary-main",
                taskStatusColor(task.status),
              )}
            >
              {lookupTaskStatus(task.status)}
            </h2>
            <div className="flex flex-row justify-between gap-2">
              {task.related && task.related.listing_status && (
                <Tooltip title={t("task_detail_tooltip_related_status")} bottom>
                  <span
                    className={clsx(
                      "w-fit text-sm ml-1 inline-flex flex-shrink-0 items-center rounded-full bg-primary-main px-1.5 py-0.5 font-medium text-white ring-1 ring-inset ring-primary-light whitespace-nowrap",
                      listingStatusColor(task.related.listing_status),
                    )}
                  >
                    {lookupListingStatus(task.related.listing_status)}
                  </span>
                </Tooltip>
              )}
              {task.related && task.related.for_sale && (
                <Tooltip title={t("task_detail_tooltip_unit_for_sale")}>
                  <div className="w-fit flex px-2 border border-error-dark rounded-lg whitespace-nowrap items-center ml-1 text-error-dark ">
                    <span className="icon-[map--real-estate-agency] text-inherit mr-1" />
                    <span className="text-sm">
                      {t("task_detail_label_for_sale")}
                    </span>
                  </div>
                </Tooltip>
              )}
            </div>
          </div>
          <p className="text-2xl lg:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            {createDateLabelForTask(task)}
            {task.is_high_priority && (
              <span className="truncate">
                ・
                <span className="text-bold text-error-dark">
                  {lookupTaskPriority(task.priority || "")}
                </span>
              </span>
            )}
          </p>
          <div className="flex flex-row items-center">
            <p className="text-2xl lg:text-3xl break-words leading-8 text-gray-600 dark:text-gray-300">
              {task.title}
            </p>
            {(!pageProps || !pageProps.isPage) && (
              <Link
                onClick={onClose}
                to={`/tasks/${task.id}`}
                className="pl-1 w-6 h-6 text-primary-main hover:text-secondary-main dark:text-white dark:hover:text-gray-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">
                  <path
                    fill="none"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2.5"
                    d="M22 3h7v7m-1.5-5.5L20 12m-3-7H8a3 3 0 0 0-3 3v16a3 3 0 0 0 3 3h16a3 3 0 0 0 3-3v-9"
                  />
                </svg>
              </Link>
            )}
          </div>
          {showTranslateLink && (
            <>
              <div className="flex flex-row items-center py-2">
                <button
                  disabled={locLoading}
                  onClick={handleTranslateClick}
                  className="underline disabled:text-gray-600 flex items-center text-lg leading-tight text-gray-900 dark:text-white text-primary-main hover:text-secondary-main"
                >
                  {checkTaskLocalized(task)
                    ? t("control_basic_untranslate")
                    : t("control_basic_translate")}
                  {locLoading ? (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="ml-1"
                      width="14"
                      height="14"
                      viewBox="0 0 32 32"
                    >
                      <circle cx="12" cy="2" r="0" fill="currentColor">
                        <animate
                          attributeName="r"
                          begin="0"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                      <circle
                        cx="12"
                        cy="2"
                        r="0"
                        fill="currentColor"
                        transform="rotate(45 12 12)"
                      >
                        <animate
                          attributeName="r"
                          begin="0.125s"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                      <circle
                        cx="12"
                        cy="2"
                        r="0"
                        fill="currentColor"
                        transform="rotate(90 12 12)"
                      >
                        <animate
                          attributeName="r"
                          begin="0.25s"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                      <circle
                        cx="12"
                        cy="2"
                        r="0"
                        fill="currentColor"
                        transform="rotate(135 12 12)"
                      >
                        <animate
                          attributeName="r"
                          begin="0.375s"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                      <circle
                        cx="12"
                        cy="2"
                        r="0"
                        fill="currentColor"
                        transform="rotate(180 12 12)"
                      >
                        <animate
                          attributeName="r"
                          begin="0.5s"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                      <circle
                        cx="12"
                        cy="2"
                        r="0"
                        fill="currentColor"
                        transform="rotate(225 12 12)"
                      >
                        <animate
                          attributeName="r"
                          begin="0.625s"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                      <circle
                        cx="12"
                        cy="2"
                        r="0"
                        fill="currentColor"
                        transform="rotate(270 12 12)"
                      >
                        <animate
                          attributeName="r"
                          begin="0.75s"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                      <circle
                        cx="12"
                        cy="2"
                        r="0"
                        fill="currentColor"
                        transform="rotate(315 12 12)"
                      >
                        <animate
                          attributeName="r"
                          begin="0.875s"
                          calcMode="spline"
                          dur="1s"
                          keySplines="0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8;0.2 0.2 0.4 0.8"
                          repeatCount="indefinite"
                          values="0;2;0;0"
                        />
                      </circle>
                    </svg>
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="ml-1"
                      width="14"
                      height="14"
                      viewBox="0 0 32 32"
                    >
                      <path
                        fill="currentColor"
                        d="M.034 16.668C.388 25.179 7.403 32 16 32s15.612-6.821 15.966-15.332A.5.5 0 0 0 32 16.5c0-.036-.013-.067-.02-.1c.003-.134.02-.265.02-.4c0-8.822-7.178-16-16-16S0 7.178 0 16c0 .135.017.266.02.4c-.007.033-.02.064-.02.1c0 .06.015.115.034.168m24.887 6.074a22 22 0 0 0-4.215-1.271c.158-1.453.251-2.962.28-4.47h4.98c-.091 2.054-.456 3.993-1.045 5.741M26.965 17h3.984a14.9 14.9 0 0 1-2.663 7.579a17 17 0 0 0-2.457-1.44c.645-1.869 1.042-3.943 1.136-6.139m-14.576 5.286A23.4 23.4 0 0 1 16 22c1.224 0 2.433.102 3.61.286C18.916 27.621 17.4 31 16 31s-2.916-3.379-3.611-8.714m1.519 8.378c-2.751-.882-5.078-3.471-6.482-6.984a21 21 0 0 1 3.99-1.217c.459 3.496 1.298 6.542 2.492 8.201m-1.634-19.955A24.4 24.4 0 0 0 16 11a24.4 24.4 0 0 0 3.726-.291c.172 1.62.274 3.388.274 5.291h-8c0-1.903.102-3.671.274-5.291M19.985 17a49 49 0 0 1-.26 4.291A24.4 24.4 0 0 0 16 21a24.4 24.4 0 0 0-3.726.291a49 49 0 0 1-.26-4.291zm.6 5.463c1.404.282 2.743.692 3.99 1.217c-1.404 3.513-3.731 6.102-6.482 6.984c1.193-1.659 2.032-4.705 2.492-8.201M21 16c0-1.836-.102-3.696-.294-5.47c1.48-.292 2.896-.72 4.215-1.271C25.605 11.288 26 13.574 26 16zm-.415-6.463c-.46-3.496-1.298-6.543-2.493-8.201c2.751.882 5.078 3.471 6.482 6.984a20.8 20.8 0 0 1-3.989 1.217m-.974.177C18.433 9.898 17.224 10 16 10s-2.433-.102-3.611-.286C13.084 4.379 14.6 1 16 1s2.916 3.379 3.611 8.714m-8.196-.177a21 21 0 0 1-3.99-1.217c1.404-3.513 3.731-6.102 6.482-6.984c-1.193 1.659-2.032 4.705-2.492 8.201m-.121.993A51 51 0 0 0 11 16H6c0-2.426.395-4.712 1.079-6.742c1.319.552 2.735.979 4.215 1.272m-.28 6.47c.029 1.508.122 3.017.28 4.471c-1.48.292-2.896.72-4.215 1.271c-.589-1.748-.954-3.687-1.045-5.742zM6.17 23.139a17 17 0 0 0-2.456 1.44A14.9 14.9 0 0 1 1.051 17h3.984c.094 2.196.491 4.27 1.135 6.139M4.313 25.38a16 16 0 0 1 2.207-1.305c1.004 2.485 2.449 4.548 4.186 5.943a15.05 15.05 0 0 1-6.393-4.638m16.981 4.637c1.738-1.394 3.182-3.458 4.186-5.943c.79.384 1.522.826 2.207 1.305a15.03 15.03 0 0 1-6.393 4.638M27 16c0-2.567-.428-4.987-1.17-7.139c.88-.422 1.698-.907 2.457-1.44A14.9 14.9 0 0 1 31 16zm.688-9.38c-.685.479-1.417.921-2.207 1.305c-1.004-2.485-2.449-4.549-4.186-5.943a15.06 15.06 0 0 1 6.393 4.638M10.706 1.983C8.968 3.377 7.524 5.441 6.52 7.926A16 16 0 0 1 4.313 6.62a15.04 15.04 0 0 1 6.393-4.637M3.714 7.421a17 17 0 0 0 2.456 1.44A22 22 0 0 0 5 16H1c0-3.19 1.009-6.145 2.714-8.579"
                      />
                    </svg>
                  )}
                </button>
              </div>
              <div className="flex flex-row">
                {translationError && (
                  <span className="text-xs text-error-dark">
                    {translationError}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <section
          id="task-details-section"
          className="grid grid-cols-6 gap-4 mt-2 border-b border-gray-300 pb-4"
        >
          {task.type && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_type")}
              </label>
              <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                {lookupTaskType(task.type as TaskFormType)}
              </p>
            </div>
          )}
          {task.sub_type && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_sub_type")}
              </label>
              <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                {lookupTaskSubType(task.type as TaskFormType, task.sub_type)}
              </p>
            </div>
          )}
          {/* For cases, we show the owner instead of the assignee. */}
          {isCase ? (
            <>
              {task.related && task.related.owner_name && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_owner")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.related.owner_name}
                  </p>
                </div>
              )}
            </>
          ) : (
            <>
              {task.assignee && task.assignee.name && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_assignee")}
                  </label>
                  {task.assignee?.user_type === "Standard" && (
                    <Link
                      onClick={() => {
                        onClose();
                      }}
                      to={`/users/${task.assignee_id}`}
                      className="underline text-lg leading-6 text-gray-900 dark:text-white text-primary-main hover:text-secondary-main"
                    >
                      {task.assignee.name}
                    </Link>
                  )}
                  {task.assignee?.user_type !== "Standard" && (
                    <h6 className="text-lg leading-6 text-gray-700 dark:text-white">
                      {lookupGroupName(
                        task.assignee.name as LocalizedGroupName,
                      )}
                    </h6>
                  )}
                </div>
              )}
            </>
          )}
          {task.reporter && task.reporter.name && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_reporter")}
              </label>
              {task.reporter?.user_type === "Standard" && (
                <Link
                  onClick={() => {
                    onClose();
                  }}
                  to={`/users/${task.reporter_id}`}
                  className="underline text-lg leading-6 text-gray-900 dark:text-white text-primary-main hover:text-secondary-main"
                >
                  {task.reporter.name}
                </Link>
              )}
              {task.reporter?.user_type !== "Standard" && (
                <h6 className="text-lg leading-6 text-gray-700 dark:text-white">
                  {task.reporter.name}
                </h6>
              )}
            </div>
          )}
          {task.type === "Maintenance" && (
            <>
              {task.billable_hours !== undefined && task.billable_hours > 0 && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_billable_hours")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {convertBillable(String(task.billable_hours))}
                  </p>
                </div>
              )}
              {task.material_costs !== undefined && task.material_costs > 0 && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_material_costs")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {convertCurrency(String(task.material_costs))}
                  </p>
                </div>
              )}
            </>
          )}
          {(task.type === "Drop" || task.type === "Pickup") &&
            task.inventory_id && (
              <div className="flex flex-col col-span-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_inventory_id")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.inventory_id}
                </p>
              </div>
            )}
          {task.start_time && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_started_on")}
              </label>
              <time
                dateTime={task.start_time}
                className="text-lg leading-6 text-gray-900 dark:text-white"
              >
                {format(new Date(task.start_time), "MM/d/yy, h:mm a")}
              </time>
            </div>
          )}
          {task.completed_datetime && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_completed_on")}
              </label>
              <time
                dateTime={task.completed_datetime}
                className="text-lg leading-6 text-gray-900 dark:text-white"
              >
                {format(new Date(task.completed_datetime), "MM/d/yy, h:mm a")}
              </time>
            </div>
          )}
          {hasAdditionalHousekeepers && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_additional_housekeepers")}
              </label>
              {task.additional_housekeepers.map((name: string, i: number) => (
                <p
                  className="sm:text-lg leading-6 text-gray-900 dark:text-white"
                  key={i}
                >
                  {name}
                </p>
              ))}
            </div>
          )}
          {hasHouseman && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_houseman")}
              </label>
              <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                {task.related.houseman}
              </p>
            </div>
          )}
          {hasInspector && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_inspector")}
              </label>
              <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                {task.related.inspector}
              </p>
            </div>
          )}
          {hasEngineer && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_engineer")}
              </label>
              <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                {task.related.engineer}
              </p>
            </div>
          )}
          {hasPreviousHousekeeper && (
            <div className="flex flex-col col-span-3">
              <label className="text-sm text-gray-600 dark:text-gray-300 truncate">
                {t("task_detail_label_previous_housekeeper")}
              </label>
              <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                {task.related.previous_housekeeper}
              </p>
            </div>
          )}
          {task.type === "Shuttle Service" && (
            <>
              {task.pickup_location && (
                <div className="flex flex-col col-span-6">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_pick_up_location")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.pickup_location}
                  </p>
                </div>
              )}
              {task.dropoff_location && (
                <div className="flex flex-col col-span-6">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_drop_off_location")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.dropoff_location}
                  </p>
                </div>
              )}
              <div className="flex flex-col col-span-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_adults")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.number_of_adults || 0}
                </p>
              </div>
              <div className="flex flex-col col-span-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_children")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.number_of_children || 0}
                </p>
              </div>
              <div className="flex flex-col col-span-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_waiver")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.waiver_verified ? "Verified" : "Unverified"}
                </p>
              </div>
            </>
          )}
          {task.description && (
            <div className="flex flex-col col-span-6">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_description")}
              </label>
              <p className="sm:text-lg break-words leading-6 text-gray-900 dark:text-white line-clamp-[40]">
                {task.description}
              </p>
            </div>
          )}
          {task.type === "Maintenance" && (
            <>
              {task.material_description !== undefined && (
                <div className="flex flex-col col-span-full">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_form_section_label_material_description")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.material_description}
                  </p>
                </div>
              )}
              {task.labor_description !== undefined && (
                <div className="flex flex-col col-span-full">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_form_section_label_labor_description")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.labor_description}
                  </p>
                </div>
              )}
            </>
          )}
          {(task.created_date || task.last_modified_date) && (
            <footer className="col-span-full">
              {task.created_date && (
                <p className="text-gray-500 text-sm truncate">
                  {t("task_detail_label_created_on")}{" "}
                  <time dateTime={task.created_date}>
                    {format(new Date(task.created_date), "PPP @ H:mm a", {
                      locale: lookupDateFNSLocale(i18n.language),
                    })}
                  </time>
                </p>
              )}
              {task.last_modified_date &&
                task.created_date !== task.last_modified_date && (
                  <p className="text-gray-500 text-sm truncate">
                    {t("task_detail_label_updated_on")}{" "}
                    <time dateTime={task.last_modified_date}>
                      {format(
                        new Date(task.last_modified_date),
                        "PPP @ H:mm a",
                        { locale: lookupDateFNSLocale(i18n.language) },
                      )}
                    </time>
                  </p>
                )}
            </footer>
          )}
        </section>
        {["Listing__c", "Listing_Order_Item__c"].includes(task.related_type) &&
          task.related && (
            <section
              id="task-related-to-listing-section"
              className="grid grid-cols-2 gap-4 mt-4 border-b border-gray-300 pb-4"
            >
              <label className="text-lg text-gray-600 dark:text-gray-300 col-span-2">
                {t("task_detail_label_related_to_listing")}
              </label>
              <div className="flex flex-col col-span-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_listing")}
                </label>
                <Link
                  onClick={onClose}
                  to={`/listings/${task.related_id}`}
                  className="underline text-lg leading-6 text-gray-900 dark:text-white text-primary-main hover:text-secondary-main"
                >
                  {(task.related as ApiListing).name_detailed}
                </Link>
                <p className="sm:text-lg leading-6 text-gray-600 dark:text-gray-300">
                  {createListingDescription(task.related)}
                </p>
              </div>
              {task.related_type === "Listing__c" && entryParkingListing && (
                <TaskEntryParkingLink
                  listing={entryParkingListing}
                  modalOpen={parkingAccessModalOpen}
                  setModalOpen={setParkingAccessModalOpen}
                />
              )}
              {task.related.check_out && (
                <div className="flex flex-col col-span-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_checkout")}
                  </label>
                  <time
                    dateTime={task.related.check_out}
                    className="text-lg leading-6 text-gray-900 dark:text-white"
                  >
                    {format(
                      new Date(task.related.check_out),
                      "MM/d/yy, h:mm a",
                      { locale: lookupDateFNSLocale(i18n.language) },
                    )}
                  </time>
                </div>
              )}
              {/* TODO: Per Dalton 9/26/24 - This 'Manager' label might not be needed. */}
              {task.related.owner_name && (
                <div className="flex flex-col col-span-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_manager")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.related.owner_name}
                  </p>
                </div>
              )}
              {task.related.next_check_in && (
                <div className="flex flex-col col-span-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_next_check_in")}
                  </label>
                  <time
                    dateTime={task.related.next_check_in}
                    className="text-lg leading-6 text-gray-900 dark:text-white"
                  >
                    {format(
                      new Date(task.related.next_check_in),
                      "MM/d/yy, h:mm a",
                      { locale: lookupDateFNSLocale(i18n.language) },
                    )}
                  </time>
                </div>
              )}
              {task.related.lessor_name && (
                <div className="flex flex-col">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_owner")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.related.lessor_name}
                  </p>
                </div>
              )}
              {showBedsInfo && (
                <div className="flex flex-col col-span-1">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_bedding")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {beddingLabel}
                  </p>
                </div>
              )}
            </section>
          )}
        {["Case"].includes(task.related_type) && task.related && (
          <section
            id="task-related-to-listing-section"
            className="grid grid-cols-2 gap-4 mt-4 border-b border-gray-300 pb-4"
          >
            <label className="text-lg text-gray-600 dark:text-gray-300 col-span-2">
              {t("task_detail_label_related_to_case")}
            </label>
            {task.related.subject && (
              <div className="flex flex-col col-span-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_subject")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.subject}
                </p>
              </div>
            )}
            {entryParkingListing && (
              <TaskEntryParkingLink
                listing={entryParkingListing}
                modalOpen={parkingAccessModalOpen}
                setModalOpen={setParkingAccessModalOpen}
              />
            )}
            {task.related.case_number && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_case_number")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.case_number || "-"}
                  {task?.related.priority === "High" && (
                    <span
                      className={
                        "icon-[solar--double-alt-arrow-up-line-duotone] text-error-dark"
                      }
                    />
                  )}
                </p>
              </div>
            )}
            {showBedsInfo && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_bedding")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white whitespace-nowrap">
                  {beddingLabel}
                </p>
              </div>
            )}
            {task.related.status && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_status")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.status}
                </p>
              </div>
            )}
            {task.related.sub_type && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_sub_type")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.sub_type}
                </p>
              </div>
            )}
            {task.related.check_out && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_checkout")}
                </label>
                <time
                  dateTime={task.related.check_out}
                  className="text-lg leading-6 text-gray-900 dark:text-white"
                >
                  {format(new Date(task.related.check_out), "MM/d/yy, h:mm a", {
                    locale: lookupDateFNSLocale(i18n.language),
                  })}
                </time>
              </div>
            )}
            {task.related.next_check_in && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_next_check_in")}
                </label>
                <time
                  dateTime={task.related.next_check_in}
                  className="text-lg leading-6 text-gray-900 dark:text-white"
                >
                  {format(
                    new Date(task.related.next_check_in),
                    "MM/d/yy, h:mm a",
                    { locale: lookupDateFNSLocale(i18n.language) },
                  )}
                </time>
              </div>
            )}
          </section>
        )}
        {["Reservation__c", "Reservation_Order_Item__c"].includes(
          task.related_type,
        ) &&
          task.related && (
            <section
              id="task-related-to-reservation"
              className="grid grid-cols-6 gap-4 mt-4 border-b border-gray-300 pb-4"
            >
              <label className="text-lg text-gray-600 dark:text-gray-300 col-span-6">
                {t("task_detail_label_related_to_reservation")}
              </label>
              {task.related.check_in_requests && (
                <div className="flex flex-col col-span-3">
                  {/* LOC: Use i18n pluralizer. There is an entire suite of tools just for this. */}
                  <label className="text-sm text-gray-600 dark:text-gray-300">{`${pluralize(
                    task.related.check_in_requests.split(";").length,
                    "Request",
                  )} (${
                    task.related.check_in_requests.split(";").length
                  })`}</label>
                  <ul className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.related.check_in_requests
                      .split(";")
                      .map((request: string, index: number) => (
                        <li className="truncate" key={`${request}-${index}`}>
                          {request}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {task.related.check_in_requests_detail && (
                <div
                  className={clsx(
                    "flex flex-col",
                    task.related.check_in_requests
                      ? "col-span-3"
                      : "col-span-6",
                  )}
                >
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_request_description")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.related.check_in_requests_detail}
                  </p>
                </div>
              )}
              <div className="flex flex-col col-span-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_reservation")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.name}
                </p>
              </div>
              {showBedsInfo && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_bedding")}
                  </label>
                  <div className="flex flex-row gap-3">
                    <p className="sm:text-lg leading-6 text-gray-900 dark:text-white whitespace-nowrap">
                      {beddingLabel}
                    </p>
                    {showMergeBeds && (
                      <Tooltip
                        top
                        noWrap
                        title={t("task_detail_tooltip_merge_beds")}
                      >
                        <Badge
                          color="blue"
                          text={
                            <span className="icon-[icon-park-outline--merge-cells] mr-0.5 text-primary-main" />
                          }
                        />
                      </Tooltip>
                    )}
                    {showSplitBeds && (
                      <Tooltip
                        top
                        noWrap
                        title={t("task_detail_tooltip_split_beds")}
                      >
                        <Badge
                          color="green"
                          text={
                            <span className="icon-[icon-park-outline--split-cells] mr-0.5 text-primary-main" />
                          }
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              )}
              <div className="flex flex-col col-span-3">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_type")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.type}
                </p>
              </div>
              {task.type === "Inspection" &&
                task.sub_type === "Final" &&
                task.related.previous_housekeeper && (
                  <div className="flex flex-col col-span-3">
                    <label className="text-sm text-gray-600 dark:text-gray-300">
                      {t("task_detail_label_previous_housekeeper")}
                    </label>
                    <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                      {task.related.previous_housekeeper}
                    </p>
                  </div>
                )}
              {task.related.listing_id && (
                <div className="flex flex-col col-span-6">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_listing")}
                  </label>
                  <Link
                    onClick={onClose}
                    to={`/listings/${task.related.listing_id}`}
                    className="underline text-lg leading-6 text-gray-900 dark:text-white text-primary-main hover:text-secondary-main"
                  >
                    {(task.related as ApiListing).name_detailed}
                  </Link>
                  <p className="sm:text-lg leading-6 text-gray-600 dark:text-gray-300">
                    {createListingDescription(task.related)}
                  </p>
                  {entryParkingListing && (
                    <TaskEntryParkingLink
                      listing={entryParkingListing}
                      modalOpen={parkingAccessModalOpen}
                      setModalOpen={setParkingAccessModalOpen}
                      className="mt-4"
                    />
                  )}
                </div>
              )}
              {task.related.check_in && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_checkin")}
                  </label>
                  <time
                    dateTime={task.related.check_in}
                    className="sm:text-lg leading-6 text-gray-900 dark:text-white"
                  >
                    {format(new Date(task.related.check_in), "M/d/yy, h:mm a", {
                      locale: lookupDateFNSLocale(i18n.language),
                    })}
                  </time>
                </div>
              )}
              {task.related.check_out && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_checkout")}
                  </label>
                  <time
                    dateTime={task.related.check_out}
                    className="sm:text-lg leading-6 text-gray-900 dark:text-white"
                  >
                    {format(
                      new Date(task.related.check_out),
                      "M/d/yy, h:mm a",
                      { locale: lookupDateFNSLocale(i18n.language) },
                    )}
                  </time>
                </div>
              )}
              {task.related.guest_name && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {shouldLookAtNextCheckInType
                      ? t("task_detail_label_next_guest")
                      : t("task_detail_label_guest")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {task.related.guest_name}
                  </p>
                </div>
              )}
              {guestTypeLabel && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {shouldLookAtNextCheckInType
                      ? t("task_detail_label_next_guest_type")
                      : t("task_detail_label_guest_type")}
                  </label>
                  <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                    {guestTypeLabel}
                  </p>
                </div>
              )}
              {typeof task.related?.check_in_gap === "number" &&
                task.related.check_in_gap > 0 && (
                  <div className="flex flex-col col-span-3">
                    <label className="text-sm text-gray-600 dark:text-gray-300">
                      {t("task_detail_label_check_in_gap")}
                    </label>
                    <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                      {task.related.check_in_gap}
                    </p>
                  </div>
                )}
              {typeof task.related?.check_out_gap === "number" &&
                task.related.check_out_gap > 0 && (
                  <div className="flex flex-col col-span-3">
                    <label className="text-sm text-gray-600 dark:text-gray-300">
                      {t("task_detail_label_check_out_gap")}
                    </label>
                    <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                      {task.related.check_out_gap}
                    </p>
                  </div>
                )}
              {task.related.previous_check_out && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_previous_check_out")}
                  </label>
                  <time
                    dateTime={task.related.previous_check_out}
                    className="sm:text-lg leading-6 text-gray-900 dark:text-white"
                  >
                    {format(
                      new Date(task.related.previous_check_out),
                      "M/d/yy, h:mm a",
                      { locale: lookupDateFNSLocale(i18n.language) },
                    )}
                  </time>
                </div>
              )}
              {task.related.next_check_in && (
                <div className="flex flex-col col-span-3">
                  <label className="text-sm text-gray-600 dark:text-gray-300">
                    {t("task_detail_label_next_check_in")}
                  </label>
                  <time
                    dateTime={task.related.next_check_in}
                    className="sm:text-lg leading-6 text-gray-900 dark:text-white"
                  >
                    {format(
                      new Date(task.related.next_check_in),
                      "M/d/yy, h:mm a",
                      { locale: lookupDateFNSLocale(i18n.language) },
                    )}
                  </time>
                </div>
              )}
            </section>
          )}
        {task.related_type === "Location" && task.related && (
          <section
            id="task-related-to-location-section"
            className="grid grid-cols-2 gap-4 mt-4 border-b border-gray-300 pb-4"
          >
            <label className="text-lg text-gray-600 dark:text-gray-300 col-span-2">
              {t("task_detail_label_related_to_location")}
            </label>
            {task.related.name && (
              <div className="flex flex-col col-span-2">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_location")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.name}
                </p>
              </div>
            )}
            {entryParkingListing && (
              <TaskEntryParkingLink
                listing={entryParkingListing}
                modalOpen={parkingAccessModalOpen}
                setModalOpen={setParkingAccessModalOpen}
                entryInstructionsLabel={t(
                  "listings_card_label_driving_directions",
                )}
              />
            )}
            {task.related.type && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_type")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.type}
                </p>
              </div>
            )}
            {task.related.parent_name && (
              <div className="flex flex-col col-span-1">
                <label className="text-sm text-gray-600 dark:text-gray-300">
                  {t("task_detail_label_parent_location")}
                </label>
                <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                  {task.related.parent_name}
                </p>
              </div>
            )}
          </section>
        )}
        {task.contact && (
          <section
            id="task-related-to-listing-section"
            className="grid grid-cols-2 gap-4 mt-4 border-b border-gray-300 pb-4"
          >
            <label className="text-lg text-gray-600 dark:text-gray-300 col-span-2">
              {t("task_detail_label_contact")}
            </label>
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_contact")}{" "}
                {task.contact.is_owner && (
                  <span className="icon-[material-symbols--star-outline]" />
                )}
              </label>
              <p className="sm:text-lg leading-6 text-gray-900 dark:text-white">
                {task.contact.name || "-"}
              </p>
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_phone")}
              </label>
              <a
                href={`tel:${task.contact.phone}`}
                className="sm:text-lg leading-6 text-primary-main hover:text-secondary-main dark:text-white"
              >
                {task.contact.phone || "-"}
              </a>
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_mobile")}
              </label>
              <a
                href={`tel:${task.contact.mobile_phone}`}
                className="sm:text-lg leading-6 text-primary-main hover:text-secondary-main dark:text-white"
              >
                {task.contact.mobile_phone || "-"}
              </a>
            </div>
            <div className="flex flex-col">
              <label className="text-sm text-gray-600 dark:text-gray-300">
                {t("task_detail_label_email")}
              </label>
              <a
                href={`mailTo:${task.contact.email}`}
                className="sm:text-lg leading-6 text-primary-main hover:text-secondary-main dark:text-white"
              >
                {task.contact.email || "-"}
              </a>
            </div>
          </section>
        )}
        {curUser && task && (
          <section id="task-related-to-reservation" className="mt-4">
            <label className="text-lg text-gray-600 dark:text-gray-300 col-span-6">
              {t("feed_widget_title")}
            </label>
            <div className="flex items-start space-x-4 mt-4">
              <div className="flex-shrink-0">
                <div className="bg-primary-main w-8 h-8 rounded-full capitalize text-white flex items-center justify-center text-center">
                  {curUser.first_name && curUser.last_name
                    ? curUser.first_name.charAt(0) +
                      "" +
                      curUser.last_name.charAt(0)
                    : "?"}
                </div>
              </div>
              <div className="min-w-0 flex-1 w-full">
                <form
                  onSubmit={postMessage}
                  className="relative"
                  id={`${task.id}-message-form-${id}`}
                >
                  <div className="overflow-hidden rounded-lg shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-primary-main">
                    <label htmlFor="comment" className="sr-only">
                      {t("feed_add_your_comment")}
                    </label>
                    <textarea
                      rows={3}
                      name="comment"
                      id={`comment-${id}`}
                      className="block w-full resize-none border-0 bg-transparent py-1.5 text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                      placeholder={t("feed_add_your_comment") + "..."}
                      value={postMessageText}
                      onChange={(e) => {
                        const val = e.currentTarget.value;
                        setPostMessageText(val);
                      }}
                      onInput={(e) => {
                        const val = e.currentTarget.value;
                        setPostMessageText(val);
                      }}
                    />

                    {/* Spacer element to match the height of the toolbar */}
                    <div className="py-2" aria-hidden="true">
                      {/* Matches height of button in toolbar (1px border + 36px content height) */}
                      <div className="py-px">
                        <div className="h-9" />
                      </div>
                    </div>
                  </div>

                  <div className="absolute inset-x-0 bottom-0 flex justify-between py-2 pl-3 pr-2">
                    <div className="flex items-center space-x-5">
                      <div className="flex items-center">
                        {uploadAllowed && (
                          <div className="w-full flex justify-end items-center text-sm leading-6 text-gray-600 dark:text-gray-300">
                            <label
                              htmlFor="file-upload"
                              className="relative flex cursor-pointer rounded-md bg-white dark:bg-gray-900 font-semibold text-primary-main focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-main focus-within:ring-offset-2 hover:text-secondary-main"
                            >
                              <span
                                className="h-5 w-5 icon-[mdi--paperclip] text-gray-500 dark:hover:text-secondary-main"
                                aria-hidden="true"
                              />
                              <input
                                disabled={attachmentsLoading}
                                id="file-upload"
                                name="file-upload"
                                type="file"
                                className="sr-only"
                                onInput={async (e) => {
                                  if (e.currentTarget.files) {
                                    setAttachmentsLoading(true);
                                    setNumberAttachments(
                                      e.currentTarget.files.length,
                                    );
                                    const filesPayload: File[] = [];
                                    let fileIndex = 0;
                                    while (
                                      fileIndex < e.currentTarget.files.length
                                    ) {
                                      filesPayload.push(
                                        e.currentTarget.files[fileIndex],
                                      );
                                      fileIndex += 1;
                                    }
                                    const uploadResults = await Promise.all(
                                      filesPayload.map((file) =>
                                        uploadFile(task.id, file),
                                      ),
                                    );

                                    // Check if any uploads failed
                                    const failedUploads = uploadResults.filter(
                                      (result) => !result.success,
                                    );
                                    if (failedUploads.length > 0) {
                                      setAttachmentsError(
                                        t("feed_error_post_attachment_failed"),
                                      );
                                      setAttachmentsLoading(false);
                                      return;
                                    }
                                    setAttachments((prev) => [
                                      ...prev,
                                      ...filesPayload.map(createFileWrapper),
                                    ]);
                                    setAttachmentsLoading(false);
                                    return;
                                  }
                                  setAttachments([]);
                                }}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <button
                        disabled={postMessageLoading}
                        form={`${task.id}-message-form-${id}`}
                        type="submit"
                        className="inline-flex items-center rounded-md bg-primary-main dark:bg-secondary-main dark:hover:bg-primary-main px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-secondary-main focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-main"
                      >
                        {postMessageLoading ? (
                          <span className="icon-[uiw--loading] animate-spin" />
                        ) : (
                          <span>{t("feed_button_submit")}</span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
                {postMessageError && (
                  <span className="text-xs text-error-dark">
                    {t("feed_error_post_message_failed")}
                  </span>
                )}
              </div>
            </div>
            {attachments.length > 0 && (
              <div
                className={clsx(
                  pageProps?.isPage
                    ? "grid-cols-3 sm:grid-cols-4 lg:grid-cols-6"
                    : "grid-cols-3",
                  "mt-2 grid grid-flow-row gap-2",
                )}
              >
                {attachments.map((attach, attachIndex) => (
                  <Attachment
                    attachment_id={attach.id}
                    loading={attachmentsLoading}
                    onDelete={deleteAllowed ? onDeleteAttachment : undefined}
                    file={attach}
                    key={`${attach.name}-${attachIndex}-preview`}
                  />
                ))}
                {attachmentsLoading && (
                  <LoadingAttachments num={numberAttachments} />
                )}
              </div>
            )}
            {(attachmentsMetaLoading || attachmentsLoading) &&
              attachments.length === 0 && (
                <div className="mt-2">
                  <LoadingAttachments num={numberAttachments} />
                </div>
              )}
            {attachmentsError && (
              <span className="text-xs text-error-dark">
                {t("feed_error_fetch_attachments")}
              </span>
            )}
            {feedMessages.length > 0 && (
              <div className="mt-4 flex flex-col gap-4">
                {feedMessages
                  .sort(
                    (a, b) =>
                      new Date(a.modstamp).getTime() -
                      new Date(b.modstamp).getTime(),
                  )
                  .map((message, index) => (
                    <div
                      key={`${message.id}-${index}`}
                      className="relative flex flex-col group"
                    >
                      <div
                        className={clsx(
                          "w-full flex gap-5",
                          curUser.sf_user_id !== message.owner_id
                            ? "flex-row"
                            : "flex-row-reverse",
                        )}
                      >
                        <div
                          className={clsx(
                            curUser.sf_user_id === message.owner_id
                              ? "bg-primary-main"
                              : "bg-success-main",
                            "flex-none w-6 h-6 text-xs rounded-full capitalize text-white flex items-center justify-center text-center",
                          )}
                        >
                          {message.owner_name
                            ? message.owner_name
                                .split(" ")
                                .map((part) => part.charAt(0))
                                .join("")
                            : "??"}
                        </div>
                        <div
                          className={clsx(
                            curUser.sf_user_id === message.owner_id
                              ? "bg-secondary-main text-white"
                              : "bg-success-main text-white",
                            "px-2 py-1.5 rounded-lg max-w-[250px]",
                          )}
                        >
                          {message.is_rich_text ? (
                            <div
                              className="text-sm"
                              dangerouslySetInnerHTML={{ __html: message.body }}
                            />
                          ) : (
                            <p className="text-sm">{message.body}</p>
                          )}
                        </div>
                      </div>
                      {message.modstamp && (
                        <p
                          className={clsx(
                            "w-full flex gap-5",
                            curUser.sf_user_id !== message.owner_id
                              ? "text-left pl-12"
                              : "text-right pr-12",
                            "-mb-4 text-xs text-gray-400 hidden group-hover:block group-focus:block animate-fade-in",
                          )}
                        >
                          {format(new Date(message.modstamp), "M/d/yy, h:mm a")}
                        </p>
                      )}
                    </div>
                  ))}
              </div>
            )}
            {getMessagesError && (
              <span className="text-xs text-error-dark">
                {t("feed_error_fetch_failed")}
              </span>
            )}
          </section>
        )}
        <div className="relative pt-[4rem] w-full">
          <footer
            className={clsx(
              "fixed w-full right-0 p-4 bottom-0 shadow-lg bg-white flex justify-between items-center",
              !pageProps?.isPage && "w-screen sm:max-w-[382px]",
            )}
          >
            <div>
              <TimedActions
                task={task}
                onClick={setAction}
                permission={taskPermission}
              />
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <SecondaryActions
                task={task}
                onClick={setAction}
                permission={taskPermission}
                selected={selectedSecondaryAction}
                setSelected={setSelectedSecondaryAction}
              />

              <PrimaryActions
                task={task}
                onClick={setAction}
                permission={taskPermission}
                onEditTask={() => {
                  // ensure the task is back to its default (english) strings before editting
                  const errorMsg = resetToDefaultText(task, feedMessages);
                  if (errorMsg) {
                    console.warn("unable to reset translations: ", errorMsg);
                  }

                  setOpenEditTask(true);
                }}
                onPreEditTask={() => {
                  //NOTE: For legacy reasons, we have different callbacks for different task states.
                  //      This one gets called when the task is in-progress, only.
                  //      The same rules apply - always reset back to English before loading edit-task.

                  // ensure the task is back to its default (english) strings before editting
                  const errorMsg = resetToDefaultText(task, feedMessages);
                  if (errorMsg) {
                    console.warn("unable to reset translations: ", errorMsg);
                  }
                }}
              />
            </div>
          </footer>
        </div>
      </div>
      {task && action && action !== "editTask" && (
        <ConfirmActionDialog
          setOpenEditTask={setOpenEditTask}
          setFollowUpTask={setFollowUpTask}
          onClose={() => {
            setSelectedSecondaryAction({
              title: t("task_command_defer"),
              action: "deferTask",
              description: t("task_command_defer_explain"),
            });
            setAction(null);
          }}
          action={action}
          fullPageMode={pageProps?.isPage || false}
          refreshTask={refreshTask}
          task={task}
        />
      )}
      {task && action && (
        <PostTask
          followUpTask={followUpTask}
          hideButton
          loading={false}
          onRefresh={async (taskData) => {
            await refreshTask(taskData);
          }}
          autoOpen={openEditTask || action === "editTask"}
          autoOnClosed={() => {
            setOpenEditTask(false);
            setFollowUpTask(false);
            setAction(null);
          }}
          task={task}
        />
      )}
    </>
  );
};
